import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { scansApi, type ScanRunDetails, type ScanRunListItem } from '@/lib/api/scans'

function formatStatus(status: string) {
  if (status === 'completed') return 'Completed'
  if (status === 'running') return 'Running'
  if (status === 'failed') return 'Failed'
  return status
}

function formatSummary(run: ScanRunListItem | ScanRunDetails) {
  if (!run.resultSummary) return '—'
  const summary = run.resultSummary
  return `${summary.totalAdded} added · ${summary.totalFound} found · ${summary.totalArchived} archived`
}

export function ScanHistoryPage() {
  const { scanId } = useParams()
  const [runs, setRuns] = useState<ScanRunListItem[]>([])
  const [selected, setSelected] = useState<ScanRunDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadRuns = useCallback(() => {
    setLoading(true)
    scansApi
      .listScans()
      .then((response) => {
        setRuns(response.items)
        setError(null)
      })
      .catch(() => setError('Failed to load scan history.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadRuns()
  }, [loadRuns])

  useEffect(() => {
    if (!scanId) {
      setSelected(null)
      return
    }

    setDetailLoading(true)
    scansApi
      .getScan(scanId)
      .then(setSelected)
      .catch(() => setError('Failed to load scan details.'))
      .finally(() => setDetailLoading(false))
  }, [scanId])

  return (
    <div className="flex h-full flex-col" data-testid="scan-history-page">
      <div className="border-b border-border px-4 py-3">
        <h1 className="text-lg font-semibold">Scan History</h1>
        <p className="text-sm text-muted-foreground">Past catalog scan runs and results</p>
      </div>

      {error ? (
        <p className="px-4 py-2 text-sm text-destructive" data-testid="scan-history-error">
          {error}
        </p>
      ) : null}

      <div className="grid min-h-0 flex-1 gap-4 overflow-auto p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <section className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">Recent runs</h2>
          </div>

          {loading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading scans...</p>
          ) : runs.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground" data-testid="scan-history-empty">
              No scans yet. Run a scan from the dashboard.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {runs.map((run) => (
                <li key={run.id}>
                  <Link
                    to={`/scans/${run.id}`}
                    className="block px-4 py-3 transition-colors hover:bg-muted/50"
                    data-testid={`scan-history-row-${run.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">
                        {new Date(run.startedAt).toLocaleString()}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        {formatStatus(run.status)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {run.createdBy} · {run.categoriesCount} categories · {formatSummary(run)}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-lg border border-border bg-card" data-testid="scan-history-detail">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">Run details</h2>
          </div>

          {!scanId ? (
            <p className="p-4 text-sm text-muted-foreground">Select a scan run to view details.</p>
          ) : detailLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading details...</p>
          ) : selected ? (
            <ScanRunDetail run={selected} />
          ) : (
            <p className="p-4 text-sm text-muted-foreground">Scan run not found.</p>
          )}
        </section>
      </div>
    </div>
  )
}

function ScanRunDetail({ run }: { run: ScanRunDetails }) {
  return (
    <div className="space-y-4 p-4 text-sm">
      <dl className="grid grid-cols-2 gap-3">
        <DetailItem label="Status" value={formatStatus(run.status)} />
        <DetailItem label="Created by" value={run.createdBy} />
        <DetailItem label="Started" value={new Date(run.startedAt).toLocaleString()} />
        <DetailItem
          label="Finished"
          value={run.finishedAt ? new Date(run.finishedAt).toLocaleString() : '—'}
        />
      </dl>

      {run.resultSummary ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Found" value={run.resultSummary.totalFound} />
          <Stat label="Added" value={run.resultSummary.totalAdded} />
          <Stat label="Updated" value={run.resultSummary.totalUpdated} />
          <Stat label="Archived" value={run.resultSummary.totalArchived} />
        </div>
      ) : null}

      {run.warnings.length > 0 ? (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Warnings
          </h3>
          <ul className="mt-2 space-y-1">
            {run.warnings.map((warning, index) => (
              <li key={`${warning.message}-${index}`} className="text-sm text-amber-700 dark:text-amber-300">
                {warning.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Items ({run.items.length})
        </h3>
        <ul className="mt-2 max-h-80 space-y-1 overflow-auto">
          {run.items.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              data-testid={`scan-item-${item.id}`}
            >
              <span className="truncate">{item.action}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{item.reason ?? '—'}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  )
}
